import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { IonItemSliding, IonItem, IonItemOptions, IonItemOption } from '@ionic/react';
import { Icon } from '../../icons';
import { Btn, Card, MetricCard, Alert, SectionHeader, FormRow, Field, FileUpload, Modal, Pagination, Badge, DataTable } from '../../components/ui';
import { ConfirmModal } from '../../components/shared';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import en from '../../locales/en.json';
import hi from '../../locales/hi.json';
import gu from '../../locales/gu.json';


import {
  getTransactions,
  getStats,
  getCategories,
  createTransaction,
  updateTransaction,
  deleteTransaction,
  updateOpeningBalance
} from '../../api/pettyCash';
import { uploadFiles, downloadFile } from '../../api/files';
import { getUsers } from '../../api/users';
import { formatDateByPreference } from '../../utils/formatters';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const translations = { en, hi, gu };

const BASE_CATEGORIES = ["Cleaning", "Repair", "Stationery", "Electricity", "Plumbing", "Security", "Festival", "Misc"];

export default function PettyCashScreen() {
  const { language = 'en' } = useSettings();
  const t = (key) => translations[language]?.petty_cash?.[key] || translations.en?.petty_cash?.[key] || key;
  const { user } = useAuth();

  const def = {
    type: "Expense",
    amount: "",
    category: "Cleaning",
    description: "",
    date: new Date().toISOString().split('T')[0],
    payment_mode: "Cash",
    reference_id: "",
    created_by: user?.name || "",
    files: [],
    file_ids: []
  };

  const [form, setForm] = useState(def);
  const [saved, setSaved] = useState(false);
  const [alertMsg, setAlertMsg] = useState("");
  const [editIdx, setEditIdx] = useState(null);
  const [deleteIdx, setDeleteIdx] = useState(null);
  const [errors, setErrors] = useState({});
  const [categories, setCategories] = useState(BASE_CATEGORIES);
  const [addingCat, setAddingCat] = useState(false);
  const [newCatValue, setNewCatValue] = useState("");
  const [prevCategory, setPrevCategory] = useState("Cleaning");
  const [editingBalance, setEditingBalance] = useState(false);
  const [newOpeningBalance, setNewOpeningBalance] = useState("");
  const [showFormModal, setShowFormModal] = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);

  // Advanced Features State
  const [filters, setFilters] = useState({ type: '', category: '', payment_mode: '', startDate: '', endDate: '' });
  const [sort, setSort] = useState({ field: 'date', order: 'desc' });
  const [page, setPage] = useState(1);
  const itemsPerPage = 15;
  const [viewTxn, setViewTxn] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showReportsMenu, setShowReportsMenu] = useState(false);
  const [showMonthlyReport, setShowMonthlyReport] = useState(false);
  const [showCategoryReport, setShowCategoryReport] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportDateRange, setExportDateRange] = useState('Current Month');
  const [exportCustomStart, setExportCustomStart] = useState('');
  const [exportCustomEnd, setExportCustomEnd] = useState('');
  const [expandedMonth, setExpandedMonth] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);

  // API Driven States
  const [txns, setTxns] = useState([]);
  const [stats, setStats] = useState({ opening_balance: 0, total_income: 0, total_expenses: 0, current_balance: 0 });
  const [usersList, setUsersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const searchInputRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState(null);

  // Load initial data
  const loadData = async () => {
    try {
      setLoading(true);
      const [txRes, statsRes, catRes, usersRes] = await Promise.all([
        getTransactions({ all: true }),
        getStats(),
        getCategories(),
        getUsers({ per_page: 100 })
      ]);

      setTxns(txRes.data || []);
      if (statsRes.data) {
        setStats(statsRes.data);
      }
      if (catRes.data) {
        setCategories(catRes.data);
      }
      setUsersList(usersRes.data || []);
    } catch (error) {
      console.error("Error loading petty cash data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetched = useRef(false);
  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    loadData();
  }, []);

  // Update default user when user object is loaded
  useEffect(() => {
    if (user?.name) {
      setForm(f => ({ ...f, created_by: user.name }));
    }
  }, [user]);

  const clearErr = key => setErrors(e => ({ ...e, [key]: "" }));
  const set = k => e => { setForm(f => ({ ...f, [k]: e.target.value })); clearErr(k); };

  const handleCategoryChange = e => {
    const val = e.target.value;
    if (val === "__add__") {
      setPrevCategory(form.category);
      setAddingCat(true);
      setNewCatValue("");
    } else {
      setForm(f => ({ ...f, category: val }));
      clearErr("category");
    }
  };

  const confirmAddCat = () => {
    const trimmed = newCatValue.trim();
    if (!trimmed) return;
    setCategories(cats => [...cats, trimmed]);
    setForm(f => ({ ...f, category: trimmed }));
    setAddingCat(false);
    clearErr("category");
  };

  const cancelAddCat = () => {
    setAddingCat(false);
    setForm(f => ({ ...f, category: prevCategory }));
  };

  const validate = () => {
    const e = {};
    if (!form.type) e.type = "Type is required";
    if (!form.amount || parseFloat(form.amount) <= 0) e.amount = "Amount must be greater than 0";
    if (!form.category) e.category = "Category is required";
    if (!form.payment_mode) e.payment_mode = "Payment mode is required";
    if (!form.date) e.date = "Date is required";
    if (!form.created_by) e.created_by = "Created by is required";
    
    if (parseFloat(form.amount) > 5000) {
      if ((!form.files || form.files.length === 0) && (!form.file_ids || form.file_ids.length === 0)) {
        e.files = "Attachment is mandatory for amounts above ₹5,000";
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handlePreSubmit = () => {
    if (!validate()) return;
    setConfirmSubmit(true);
  };

  const submit = async () => {

    try {
      setSubmitting(true);
      let uploadedFileIds = [...(form.file_ids || [])];

      // 1. Upload files first if any new files are selected
      if (form.files && form.files.length > 0) {
        const uploadRes = await uploadFiles(form.files, 'petty_cash');
        if (uploadRes.success) {
          // Extract IDs robustly. FileController returns { success: true, data: { uploaded: [...] } }
          // uploadRes is already the content of the response due to axios interceptor
          const uploadedArray = uploadRes.data?.uploaded || uploadRes.uploaded || [];
          const newIds = uploadedArray.map(f => f.id).filter(id => id !== undefined);
          uploadedFileIds = [...uploadedFileIds, ...newIds];
        }
      }

      // 2. Submit transaction payload
      const payload = {
        type: form.type,
        amount: parseFloat(form.amount) || 0,
        category: form.category,
        description: form.description,
        date: form.date,
        payment_mode: form.payment_mode,
        reference_id: form.reference_id,
        created_by: form.created_by,
        file_ids: uploadedFileIds
      };

      if (editIdx !== null) {
        const txnId = txns[editIdx].id;
        await updateTransaction(txnId, payload);
        setAlertMsg("Transaction updated successfully!");
        setEditIdx(null);
      } else {
        await createTransaction(payload);
        setAlertMsg("Transaction recorded successfully!");
      }

      // 3. Reset form and refresh details
      setForm({
        ...def,
        created_by: user?.name || ""
      });
      setSaved(true);
      setErrors({});
      setShowFormModal(false);
      setTimeout(() => setSaved(false), 3000);
      await loadData();
    } catch (error) {
      console.error("Error submitting petty cash transaction:", error);
      setErrors({ api: error.message || "Something went wrong. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = i => {
    const t = txns[i];
    setForm({
      type: t.type,
      amount: String(t.amount),
      category: t.category,
      description: t.description || "",
      date: t.date,
      payment_mode: t.payment_mode,
      reference_id: t.reference_id || "",
      created_by: t.created_by,
      files: [],
      file_ids: t.file_ids || []
    });
    setEditIdx(i);
    setErrors({});
    setAddingCat(false);
    setShowFormModal(true);
  };

  const confirmDelete = async () => {
    try {
      const txnId = txns[deleteIdx].id;
      await deleteTransaction(txnId);
      setDeleteIdx(null);
      setAlertMsg("Transaction deleted successfully!");
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      await loadData();
    } catch (error) {
      console.error("Error deleting transaction:", error);
    }
  };

  const handleUpdateOpeningBalance = async () => {
    try {
      setSubmitting(true);
      await updateOpeningBalance(newOpeningBalance);
      setEditingBalance(false);
      setAlertMsg("Opening balance updated!");
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      await loadData();
    } catch (error) {
      console.error("Error updating opening balance:", error);
      setErrors({ api: "Failed to update opening balance" });
    } finally {
      setSubmitting(false);
    }
  };

  // Extract creators list from users
  const creatorOptions = Array.from(new Set([
    ...usersList.map(u => u.name)
  ]));

  // Advanced Filtering, Sorting, and Pagination
  const filteredAndSortedTxns = txns.filter(t => {
    // Search Query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = t.description?.toLowerCase().includes(query) ||
        t.category?.toLowerCase().includes(query) ||
        t.created_by?.toLowerCase().includes(query) ||
        t.payment_mode?.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }

    // Advanced Filters
    if (filters.type && filters.type !== 'All' && t.type !== filters.type) return false;
    if (filters.category && filters.category !== 'All' && t.category !== filters.category) return false;
    if (filters.payment_mode && filters.payment_mode !== 'All' && t.payment_mode !== filters.payment_mode) return false;

    if (filters.startDate && new Date(t.date) < new Date(filters.startDate)) return false;
    if (filters.endDate && new Date(t.date) > new Date(filters.endDate)) return false;

    return true;
  }).sort((a, b) => {
    // Sorting
    let valA = a[sort.field];
    let valB = b[sort.field];

    if (sort.field === 'amount') {
      valA = Number(valA);
      valB = Number(valB);
    } else if (sort.field === 'date') {
      valA = new Date(valA).getTime();
      valB = new Date(valB).getTime();
    } else {
      valA = String(valA || '').toLowerCase();
      valB = String(valB || '').toLowerCase();
    }

    if (valA < valB) return sort.order === 'asc' ? -1 : 1;
    if (valA > valB) return sort.order === 'asc' ? 1 : -1;
    return 0;
  });

  const paginatedTxns = filteredAndSortedTxns.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const handleExportPDF = () => {
    let txnsToExport = [...txns];
    
    // Filter based on selected date range
    const now = new Date();
    let startDate = null;
    let endDate = null;

    if (exportDateRange === 'Current Month') {
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
    } else if (exportDateRange === 'Previous Month') {
      startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
    } else if (exportDateRange === 'Custom Range') {
      if (exportCustomStart) startDate = new Date(exportCustomStart);
      if (exportCustomEnd) {
        endDate = new Date(exportCustomEnd);
        endDate.setHours(23, 59, 59);
      }
    }

    if (startDate || endDate) {
      txnsToExport = txnsToExport.filter(t => {
        const d = new Date(t.date);
        if (startDate && d < startDate) return false;
        if (endDate && d > endDate) return false;
        return true;
      });
    }

    // Sort by date desc
    txnsToExport.sort((a, b) => new Date(b.date) - new Date(a.date));

    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(`Petty Cash Ledger (${exportDateRange})`, 14, 15);
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 22);

    const tableColumn = ["Date", "Description", "Category", "Mode", "By", "Type", "Amount"];
    const tableRows = [];

    txnsToExport.forEach(txn => {
      const txnData = [
        formatDateByPreference(txn.date),
        txn.description || txn.category,
        txn.category,
        txn.payment_mode,
        txn.created_by,
        txn.type,
        txn.amount
      ];
      tableRows.push(txnData);
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 28,
      theme: 'grid',
      styles: { fontSize: 8 },
      headStyles: { fillColor: [41, 128, 185] },
    });

    doc.save(`petty_cash_${exportDateRange.replace(/ /g, '_').toLowerCase()}.pdf`);
    setShowExportModal(false);
  };

  const getMonthlyData = () => {
    const data = {};
    txns.forEach(t => {
      if (t.type === 'Expense') {
        const month = new Date(t.date).toLocaleString('default', { month: 'long', year: 'numeric' });
        if (!data[month]) data[month] = { amount: 0, transactions: [] };
        data[month].amount += Number(t.amount);
        data[month].transactions.push(t);
      }
    });
    return Object.entries(data).sort((a, b) => new Date(b[0]) - new Date(a[0]));
  };

  const getCategoryData = () => {
    const data = {};
    txns.forEach(t => {
      if (!data[t.category]) data[t.category] = { Income: 0, Expense: 0, transactions: [] };
      data[t.category][t.type] += Number(t.amount);
      data[t.category].transactions.push(t);
    });
    return Object.entries(data);
  };

  // Reset page if filtered results change and current page is empty
  useEffect(() => {
    if (page > 1 && paginatedTxns.length === 0 && filteredAndSortedTxns.length > 0) {
      setPage(1);
    }
  }, [filteredAndSortedTxns.length, page, paginatedTxns.length]);

  const renderForm = () => (
    <>
      <SectionHeader
        title={editIdx !== null ? t("title_edit_txn") : t("btn_add_txn")}
        action={editIdx !== null && (
          <Btn xs onClick={() => { setEditIdx(null); setForm({ ...def, created_by: user?.name || "" }); setErrors({}); setShowFormModal(false); }}>
            Cancel Edit
          </Btn>
        )}
      />

      <FormRow cols={2}>
        <div>
          <Field label="Type" type="select" value={form.type} onChange={e => { set("type")(e); }} options={[t("type_expense"), t("type_income")]} />
          {errors.type && <p className="text-xs text-red-500 mt-1">{errors.type}</p>}
        </div>
        <div>
          <Field label={t("label_amount_rs")} type="number" placeholder="0.00" value={form.amount} onChange={set("amount")} />
          {errors.amount && <p className="text-xs text-red-500 mt-1">{errors.amount}</p>}
        </div>
      </FormRow>

      <FormRow cols={2}>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">{t("label_category")}</label>
          {addingCat ? (
            <div className="flex gap-1.5 items-center">
              <input
                type="text"
                value={newCatValue}
                onChange={e => setNewCatValue(e.target.value)}
                onKeyDown={e => e.key === "Enter" && confirmAddCat()}
                placeholder={t("placeholder_new_category")}
                className="flex-1 px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                autoFocus
              />
              <button onClick={confirmAddCat} className="px-2.5 py-2 bg-primary text-white text-xs font-semibold rounded-lg cursor-pointer border-0 hover:bg-primary/90">{t("btn_add")}</button>
              <button onClick={cancelAddCat} className="text-xs text-muted hover:text-ink cursor-pointer bg-transparent border-0 px-1">{t("btn_cancel")}</button>
            </div>
          ) : (
            <select
              value={form.category}
              onChange={handleCategoryChange}
              className={`w-full px-3 py-2.5 border rounded-lg text-sm text-ink bg-white dark:bg-slate-800 dark:text-white transition-all placeholder:text-slate-300 dark:placeholder-slate-500 focus:outline-none focus:ring-2 ${errors.category ? "border-red-500 focus:ring-red-200 focus:border-red-500" : "border-border dark:border-slate-700 focus:ring-primary/20 focus:border-primary"}`}
            >
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
              <option value="__add__">+ Add New Category</option>
            </select>
          )}
          {errors.category && <p className="text-xs text-red-500 mt-1">{errors.category}</p>}
        </div>
        <div>
          <Field label={t("label_payment_mode")} type="select" value={form.payment_mode} onChange={set("payment_mode")} options={[t("mode_cash"), "UPI", "Bank Transfer"]} />
          {errors.payment_mode && <p className="text-xs text-red-500 mt-1">{errors.payment_mode}</p>}
        </div>
      </FormRow>

      <FormRow cols={2}>
        <div>
          <Field label="Ref / Transaction ID" placeholder="e.g. TXN1234567890" value={form.reference_id || ""} onChange={set("reference_id")} />
          {errors.reference_id && <p className="text-xs text-red-500 mt-1">{errors.reference_id}</p>}
        </div>
        <div>
          <Field label="Date" type="date" value={form.date} onChange={set("date")} />
          {errors.date && <p className="text-xs text-red-500 mt-1">{errors.date}</p>}
        </div>
      </FormRow>

      <FormRow>
        <div>
          <Field label="Description" placeholder={t("placeholder_describe")} value={form.description} onChange={set("description")} />
          {errors.description && <p className="text-xs text-red-500 mt-1">{errors.description}</p>}
        </div>
      </FormRow>

      {editIdx !== null && form.file_ids && form.file_ids.length > 0 && (
        <div className="mb-4 bg-slate-50 border border-slate-100 p-3 rounded-xl dark:bg-slate-800 dark:border-slate-700">
          <label className="block text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">{t("title_attached_receipts")}</label>
          <div className="space-y-1.5">
            {form.file_ids.map(fid => {
              const fileObj = (txns[editIdx]?.attachments || txns[editIdx]?.files)?.find(f => f.id === fid);
              if (!fileObj) return null;
              return (
                <div key={fid} className="flex items-center justify-between p-2 bg-white rounded-lg border border-slate-200 hover:border-slate-300 transition-colors dark:bg-slate-800 dark:border-slate-700">
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon name={fileObj.extension === 'pdf' ? 'documents' : 'eye'} size={12} className="text-primary flex-shrink-0" />
                    <span className="text-xs font-semibold text-slate-600 truncate dark:text-white">{fileObj.original_name}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => downloadFile(fileObj.id, fileObj.original_name)}
                      className="p-1 hover:bg-slate-100 text-primary rounded cursor-pointer transition-colors bg-transparent border-0"
                      title={t("title_download_receipt")}
                    >
                      <Icon name="download" size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setForm(f => ({
                          ...f,
                          file_ids: f.file_ids.filter(id => id !== fid)
                        }));
                      }}
                      className="p-1 hover:bg-red-50 text-red-500 rounded cursor-pointer transition-colors bg-transparent border-0"
                      title={t("title_remove_attachment")}
                    >
                      <Icon name="trash" size={12} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mb-4">
        <FileUpload
          multiple={true}
          label={t("label_attach_receipt")}
          accept="image/*,.pdf"
          files={form.files}
          onChange={files => setForm(f => ({ ...f, files }))}
          note={t("note_upload_bills")}
        />
        {errors.files && <p className="text-xs text-red-500 mt-2 font-semibold">{errors.files}</p>}
      </div>

      <Btn
        variant="primary"
        full
        icon={editIdx !== null ? "check" : "plus"}
        onClick={handlePreSubmit}
        disabled={submitting}
      >
        {submitting
          ? (editIdx !== null ? t("btn_updating") : t("btn_adding"))
          : (editIdx !== null ? t("btn_update_txn") : t("btn_add_txn"))
        }
      </Btn>
    </>
  );

  return (
    <div className="fade-up space-y-4 pb-20 lg:pb-0">
      {deleteIdx !== null && (
        <ConfirmModal
          title={t("title_delete_txn")}
          message={`Delete "${txns[deleteIdx]?.description || txns[deleteIdx]?.category}"? This cannot be undone.`}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteIdx(null)}
        />
      )}

      {confirmSubmit && (
        <ConfirmModal
          title={editIdx !== null ? "Confirm Update" : "Confirm Save"}
          message={`Are you sure you want to ${editIdx !== null ? "update" : "save"} this transaction?`}
          onConfirm={() => {
            setConfirmSubmit(false);
            submit();
          }}
          onCancel={() => setConfirmSubmit(false)}
        />
      )}

      {saved && <Alert type="success" onClose={() => setSaved(false)}>{alertMsg}</Alert>}
      {errors.api && <Alert type="danger" onClose={() => clearErr('api')}>{errors.api}</Alert>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="relative group">
          <MetricCard loading={loading} label={t("label_opening_balance")} value={`₹${Number(stats.opening_balance).toLocaleString()}`} icon="bank" accent="blue" />
          <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => { setNewOpeningBalance(stats.opening_balance); setEditingBalance(true); }}
              className="p-1.5 bg-white/80 hover:bg-white text-primary rounded shadow-sm transition-colors border border-slate-200"
            >
              <Icon name="edit" size={14} />
            </button>
          </div>
        </div>
        <MetricCard loading={loading} label={t("label_total_income")} value={`₹${Number(stats.total_income).toLocaleString()}`} delta="This month" deltaUp icon="trend_up" accent="green" />
        <MetricCard loading={loading} label={t("label_total_expenses")} value={`₹${Number(stats.total_expenses).toLocaleString()}`} delta="This month" deltaUp={false} icon="trend_down" accent="red" />
        <MetricCard loading={loading} label={t("label_current_balance")} value={`₹${Number(stats.current_balance).toLocaleString()}`} icon="money" accent="amber" />
      </div>

      <div className="grid grid-cols-1 gap-4">
        {/* Transaction Ledger Card */}
        <Card>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
            <SectionHeader
              title={t("title_transaction_ledger")}
              subtitle={`${filteredAndSortedTxns.length} records · Balance: ₹${Number(stats.current_balance).toLocaleString()}`}
            />
            {mobileSearchOpen ? (
              <div className="flex items-center gap-2 w-full md:hidden fade-in">
                <div className="flex-1 relative h-[42px]">
                  <Icon name="search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder={t("placeholder_search_ledger")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-8 h-full border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 bg-slate-50/50 hover:bg-slate-50 transition-all placeholder:text-slate-400 dark:bg-slate-800 dark:text-white"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery("")}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-sm p-1"
                    >
                      ×
                    </button>
                  )}
                </div>
                <Btn
                  variant="secondary"
                  className="h-[42px] px-3 shrink-0"
                  onClick={() => setMobileSearchOpen(false)}
                >
                  <Icon name="x" size={16} />
                </Btn>
              </div>
            ) : (
              <div className="flex gap-2 w-full md:w-auto items-center">
                {/* On mobile: search icon button */}
                <Btn
                  variant={searchQuery ? "primary" : "secondary"}
                  className="h-[42px] px-3 shrink-0 md:hidden relative"
                  onClick={() => {
                    setMobileSearchOpen(true);
                    setTimeout(() => searchInputRef.current?.focus(), 80);
                  }}
                  title="Search"
                >
                  <Icon name="search" size={18} />
                  {searchQuery && (
                    <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white dark:border-slate-900"></span>
                  )}
                </Btn>

                {/* On desktop: full search input */}
                <div className="hidden md:block w-64 relative h-[42px]">
                  <Icon name="search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder={t("placeholder_search_ledger")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3 h-full border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 bg-slate-50/50 hover:bg-slate-50 transition-all placeholder:text-slate-400 dark:bg-slate-800 dark:text-white"
                  />
                </div>

                <Btn
                  variant={showFilters ? "primary" : "secondary"}
                  className="relative h-[42px] px-3 shrink-0"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Icon name="filter" size={18} />
                  {(filters.type || filters.category || filters.payment_mode || filters.startDate || filters.endDate) && (
                    <span className="absolute -top-1.5 -right-1.5 w-3 h-3 bg-red-500 rounded-full border-2 border-white dark:border-slate-900"></span>
                  )}
                </Btn>
                <div className="relative shrink-0">
                  <Btn
                    variant="secondary"
                    className="h-[42px] px-3 shrink-0"
                    onClick={() => setShowReportsMenu(!showReportsMenu)}
                  >
                    <div className='flex items-center gap-1'>
                      <Icon name="documents" size={18} />
                      <span className="hidden md:inline ml-1.5">Reports</span>
                    </div>
                  </Btn>
                  {showReportsMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-100 dark:border-slate-700 z-50 overflow-hidden">
                      <button onClick={() => { setShowReportsMenu(false); setShowExportModal(true); }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-700/50 flex items-center gap-2 cursor-pointer border-0 bg-transparent text-ink dark:text-white">
                        <Icon name="download" size={16} className="text-slate-500" /> Export to PDF
                      </button>
                      <button onClick={() => { setShowReportsMenu(false); setShowMonthlyReport(true); }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-700/50 flex items-center gap-2 cursor-pointer border-0 bg-transparent text-ink dark:text-white border-t border-slate-100 dark:border-slate-700">
                        <Icon name="calendar" size={16} className="text-slate-500" /> Monthly Expense
                      </button>
                      <button onClick={() => { setShowReportsMenu(false); setShowCategoryReport(true); }} className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-700/50 flex items-center gap-2 cursor-pointer border-0 bg-transparent text-ink dark:text-white border-t border-slate-100 dark:border-slate-700">
                        <Icon name="pie_chart" size={16} className="text-slate-500" /> Category Report
                      </button>
                    </div>
                  )}
                </div>
                <Btn
                  variant="primary"
                  className="h-[42px] px-3 md:px-4 shrink-0"
                  onClick={() => {
                    setEditIdx(null);
                    setForm({ ...def, created_by: user?.name || "" });
                    setErrors({});
                    setShowFormModal(true);
                  }}
                >
                  <div className="flex items-center gap-1.5">
                    <Icon name="plus" size={18} />
                    <span className="hidden md:inline">Add Transaction</span>
                  </div>
                </Btn>
              </div>
            )}
          </div>

          {showFilters && (
            <div className="mb-4 p-4 rounded-xl border border-border bg-slate-50 dark:bg-slate-800/50 grid grid-cols-2 lg:grid-cols-5 gap-3">
              <Field
                label="Type"
                type="select"
                value={filters.type}
                onChange={e => setFilters(f => ({ ...f, type: e.target.value }))}
                options={[{ label: 'All', value: '' }, { label: 'Income', value: 'Income' }, { label: 'Expense', value: 'Expense' }]}
              />
              <Field
                label="Category"
                type="select"
                value={filters.category}
                onChange={e => setFilters(f => ({ ...f, category: e.target.value }))}
                options={[{ label: 'All', value: '' }, ...categories.map(c => ({ label: c, value: c }))]}
              />
              <Field
                label="Payment Mode"
                type="select"
                value={filters.payment_mode}
                onChange={e => setFilters(f => ({ ...f, payment_mode: e.target.value }))}
                options={[{ label: 'All', value: '' }, { label: 'Cash', value: 'Cash' }, { label: 'Bank Transfer', value: 'Bank Transfer' }, { label: 'Cheque', value: 'Cheque' }]}
              />
              <Field
                label="Start Date"
                type="date"
                value={filters.startDate}
                onChange={e => setFilters(f => ({ ...f, startDate: e.target.value }))}
              />
              <Field
                label="End Date"
                type="date"
                value={filters.endDate}
                onChange={e => setFilters(f => ({ ...f, endDate: e.target.value }))}
              />
              <div className="col-span-2 lg:col-span-5 flex flex-wrap gap-3 items-center mt-2 border-t border-slate-200 dark:border-slate-700 pt-3">
                <span className="text-sm font-medium text-slate-500">Sort By:</span>
                <select
                  value={sort.field}
                  onChange={(e) => setSort(s => ({ ...s, field: e.target.value }))}
                  className="bg-white dark:bg-slate-900 border border-border rounded-lg text-sm px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary dark:text-white"
                >
                  <option value="date">Date</option>
                  <option value="amount">Amount</option>
                  <option value="category">Category</option>
                </select>
                <button
                  onClick={() => setSort(s => ({ ...s, order: s.order === 'asc' ? 'desc' : 'asc' }))}
                  className="p-1.5 rounded-lg border border-border bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                >
                  <Icon name={sort.order === 'asc' ? 'arrow_up' : 'arrow_down'} size={16} className="text-slate-500" />
                </button>
                <div className="flex-1"></div>
                <Btn xs variant="secondary" onClick={() => {
                  setFilters({ type: '', category: '', payment_mode: '', startDate: '', endDate: '' });
                  setSearchQuery('');
                }}>
                  Clear Filters
                </Btn>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400 space-y-2">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-xs font-semibold">{t("msg_loading")}</p>
            </div>
          ) : filteredAndSortedTxns.length === 0 ? (
            <div className="text-center py-16 border-2 border-dashed border-slate-100 dark:border-slate-800/60 rounded-2xl">
              <div className="w-12 h-12 bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-700/50 rounded-full flex items-center justify-center mx-auto mb-3">
                <Icon name="bank" size={20} className="text-slate-400 dark:text-slate-500" />
              </div>
              <h3 className="text-sm font-bold text-ink dark:text-slate-200 mb-1">{t("msg_no_txns")}</h3>
              <p className="text-xs text-muted dark:text-slate-400">{t("msg_adjust_search")}</p>
            </div>
          ) : (
            <>
              {/* Desktop View */}
              <div className="hidden lg:block -mx-5 -mb-5 mt-2">
                <DataTable
                  cols={['', 'Description', 'Category', 'Date', 'Mode', 'By', 'Docs', 'Amount', '']}
                  rows={paginatedTxns.map((txn, i) => [
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold ${txn.type === "Income" ? "gradient-success" : "gradient-danger"}`}>
                      {txn.type === "Income" ? <Icon name="trend_up" size={14} color="white" /> : <Icon name="trend_down" size={14} color="white" />}
                    </div>,
                    <div className="font-semibold text-ink truncate max-w-[200px]">{txn.description || txn.category}</div>,
                    <span className="text-slate-500 font-medium">{txn.category}</span>,
                    <span className="whitespace-nowrap">{formatDateByPreference(txn.date)}</span>,
                    <span>{txn.payment_mode}</span>,
                    <span>{txn.created_by}</span>,
                    ((txn.attachments && txn.attachments.length > 0) || (txn.files && txn.files.length > 0)) ? (
                      <div
                        onClick={(e) => { e.stopPropagation(); setSelectedFiles(txn.attachments || txn.files); }}
                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-bold text-primary bg-primary/10 hover:bg-primary/20 cursor-pointer transition-all"
                        title={t("title_view_receipts")}
                      >
                        <Icon name="documents" size={10} />
                        <span>{(txn.attachments || txn.files).length}</span>
                      </div>
                    ) : <span className="text-muted">-</span>,
                    <div className={`font-bold whitespace-nowrap ${txn.type === "Income" ? "text-emerald-600" : "text-red-500"}`}>
                      {txn.type === "Income" ? "+" : "-"}₹{Number(txn.amount).toLocaleString()}
                    </div>,
                    <div className="flex gap-1 justify-end">
                      <Btn xs icon="eye" onClick={() => setViewTxn(txn)}>View</Btn>
                      <Btn xs icon="edit" onClick={() => startEdit(txns.indexOf(txn))}>Edit</Btn>
                      <Btn xs variant="danger" icon="trash" onClick={() => setDeleteIdx(txns.indexOf(txn))}>Delete</Btn>
                    </div>
                  ])}
                />
              </div>

              {/* Mobile View (Cards) */}
              <div className="block lg:hidden space-y-3 pb-8">
                {paginatedTxns.map((txn, i) => (
                  <IonItemSliding key={txn.id || i} className={`rounded-xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden ${i === 0 ? 'animate-swipe-hint' : ''}`} style={{ '--background': 'transparent' }}>
                    <IonItem lines="none" style={{ '--background': 'transparent', '--padding-start': '0', '--inner-padding-end': '0' }}>
                      <div className="flex w-full items-center gap-3 p-3 bg-white dark:bg-slate-900">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${txn.type === "Income" ? "gradient-success" : "gradient-danger"}`}>
                          {txn.type === "Income" ? <Icon name="trend_up" size={16} color="white" /> : <Icon name="trend_down" size={16} color="white" />}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-ink truncate">{txn.description || txn.category}</div>
                          <div className="text-xs text-muted mt-1 leading-tight">
                            <span className="font-semibold text-slate-500">{txn.category}</span><br />
                            {formatDateByPreference(txn.date)} · {txn.payment_mode}
                          </div>
                        </div>

                        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                          <div className={`text-sm font-bold ${txn.type === "Income" ? "text-emerald-600" : "text-red-500"}`}>
                            {txn.type === "Income" ? "+" : "-"}₹{Number(txn.amount).toLocaleString()}
                          </div>

                          {((txn.attachments && txn.attachments.length > 0) || (txn.files && txn.files.length > 0)) && (
                            <div
                              onClick={(e) => { e.stopPropagation(); setSelectedFiles(txn.attachments || txn.files); }}
                              className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold text-primary bg-primary/10 border border-primary/5 cursor-pointer"
                            >
                              <Icon name="documents" size={10} />
                              <span>{(txn.attachments || txn.files).length}</span>
                            </div>
                          )}
                        </div>

                        <div className="text-slate-300 ml-1">
                          <Icon name="chevron_left" size={14} />
                        </div>
                      </div>
                    </IonItem>

                    <IonItemOptions side="end">
                      <IonItemOption color="success" onClick={() => setViewTxn(txn)}>
                        <div className="flex flex-col items-center justify-center w-12">
                          <Icon name="eye" size={20} color="white" />
                        </div>
                      </IonItemOption>
                      <IonItemOption color="primary" onClick={() => startEdit(txns.indexOf(txn))}>
                        <div className="flex flex-col items-center justify-center w-12">
                          <Icon name="edit" size={20} color="white" />
                        </div>
                      </IonItemOption>
                      <IonItemOption color="danger" onClick={() => setDeleteIdx(txns.indexOf(txn))}>
                        <div className="flex flex-col items-center justify-center w-12">
                          <Icon name="trash" size={20} color="white" />
                        </div>
                      </IonItemOption>
                    </IonItemOptions>
                  </IonItemSliding>
                ))}
              </div>
              <Pagination page={page} total={filteredAndSortedTxns.length} perPage={itemsPerPage} onPage={setPage} />
            </>
          )}
        </Card>
      </div>

      {/* View Transaction Details Modal */}
      {viewTxn && (
        <Modal title="Transaction Details" onClose={() => setViewTxn(null)}>
          <div className="space-y-4">
            <div className="flex justify-between items-start pb-4 border-b border-border">
              <div>
                <h3 className="font-bold text-ink text-lg">{viewTxn.description || viewTxn.category}</h3>
                <div className="text-sm text-muted mt-1">{formatDateByPreference(viewTxn.date)}</div>
              </div>
              <Badge color={viewTxn.type === 'Income' ? 'success' : 'danger'}>
                {viewTxn.type}
              </Badge>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-xs text-muted block mb-1">Amount</span>
                <span className={`font-bold ${viewTxn.type === 'Income' ? 'text-emerald-600' : 'text-red-500'}`}>
                  {viewTxn.type === 'Income' ? '+' : '-'}₹{Number(viewTxn.amount).toLocaleString()}
                </span>
              </div>
              <div>
                <span className="text-xs text-muted block mb-1">Category</span>
                <span className="font-semibold text-ink">{viewTxn.category}</span>
              </div>
              <div>
                <span className="text-xs text-muted block mb-1">Payment Mode</span>
                <span className="font-semibold text-ink">{viewTxn.payment_mode}</span>
              </div>
              {viewTxn.reference_id && (
                <div>
                  <span className="text-xs text-muted block mb-1">Ref / Txn ID</span>
                  <span className="font-semibold text-ink">{viewTxn.reference_id}</span>
                </div>
              )}
              <div>
                <span className="text-xs text-muted block mb-1">Created By</span>
                <span className="font-semibold text-ink">{viewTxn.created_by}</span>
              </div>
            </div>

            {((viewTxn.attachments && viewTxn.attachments.length > 0) || (viewTxn.files && viewTxn.files.length > 0)) && (
              <div className="pt-4 border-t border-border">
                <span className="text-sm font-semibold text-ink block mb-3">Attached Receipts / Invoices</span>
                <div className="space-y-2">
                  {(viewTxn.attachments || viewTxn.files).map((file, i) => (
                    <div key={file.id || i} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Icon name="documents" size={16} className="text-primary" />
                        <span className="text-sm font-medium">{file.original_name}</span>
                      </div>
                      <Btn xs variant="secondary" onClick={() => downloadFile(file.id, file.original_name)}>
                        Download
                      </Btn>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Receipts / Invoices View Modal */}
      {selectedFiles && (
        <Modal title={t("title_view_attached_receipts")} onClose={() => setSelectedFiles(null)}>
          <div className="space-y-2.5">
            {selectedFiles.map((file, i) => (
              <div key={file.id || i} className="flex items-center justify-between p-3.5 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl hover:border-primary/20 transition-all">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-primary dark:text-white border border-blue-100 dark:border-primary flex items-center justify-center text-primary flex-shrink-0">
                    <Icon name={file.extension === 'pdf' ? 'documents' : 'eye'} size={18} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-ink truncate">{file.original_name}</div>
                    <div className="text-xs text-muted mt-0.5">{(file.size / 1024).toFixed(1)} KB · {file.extension.toUpperCase()}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const url = file.temporary_url || file.file_url;
                      if (url) window.open(url, '_blank');
                    }}
                    className="px-3 py-2 bg-primary hover:bg-primary/90 text-white text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 cursor-pointer">
                    <Icon name="eye" size={12} />
                    View
                  </button>
                  {/* <button
                    onClick={() => downloadFile(file.id, file.original_name)}
                    className="px-3 py-2 bg-primary text-white text-xs font-bold rounded-lg hover:bg-primary/90 transition-all flex items-center gap-1.5 cursor-pointer border-0 shadow-sm"
                  >
                    <Icon name="download" size={12} />
                    Download
                  </button> */}
                </div>
              </div>
            ))}
          </div>
        </Modal>
      )}

      {/* Edit Opening Balance Modal */}
      {editingBalance && (
        <Modal title={t("title_update_opening_balance")} onClose={() => setEditingBalance(false)}>
          <div className="p-1">
            <Field
              label={t("label_opening_balance_rs")}
              type="number"
              value={newOpeningBalance}
              onChange={e => setNewOpeningBalance(e.target.value)}
              placeholder="e.g. 25000"
            />
            <p className="text-xs text-muted mt-2 mb-5">
              Set the initial cash-in-hand balance. Leave empty to automatically use your Primary Bank Account's balance.
            </p>
            <div className="flex gap-2 justify-end">
              <Btn variant="outline" onClick={() => setEditingBalance(false)}>{t("btn_cancel")}</Btn>
              <Btn variant="primary" onClick={handleUpdateOpeningBalance} disabled={submitting || !newOpeningBalance}>
                {submitting ? t("btn_updating") : "Update Balance"}
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* Export to PDF Modal */}
      {showExportModal && (
        <Modal title="Export Ledger to PDF" onClose={() => setShowExportModal(false)}>
          <div className="space-y-4">
            <Field 
              label="Date Range" 
              type="select" 
              value={exportDateRange} 
              onChange={(e) => setExportDateRange(e.target.value)}
              options={["All Time", "Current Month", "Previous Month", "Custom Range"]}
            />
            
            {exportDateRange === 'Custom Range' && (
              <FormRow cols={2}>
                <div>
                  <Field label="Start Date" type="date" value={exportCustomStart} onChange={e => setExportCustomStart(e.target.value)} />
                </div>
                <div>
                  <Field label="End Date" type="date" value={exportCustomEnd} onChange={e => setExportCustomEnd(e.target.value)} />
                </div>
              </FormRow>
            )}

            <div className="pt-2 flex justify-end gap-2">
              <Btn variant="outline" onClick={() => setShowExportModal(false)}>Cancel</Btn>
              <Btn variant="primary" icon="download" onClick={handleExportPDF}>Download PDF</Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* Monthly Expense Report Modal */}
      {showMonthlyReport && (
        <Modal title="Monthly Expense Report" onClose={() => { setShowMonthlyReport(false); setExpandedMonth(null); }}>
          <div className="space-y-3">
            {getMonthlyData().length > 0 ? (
              <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Month</th>
                      <th className="px-4 py-3 font-semibold text-right">Total Expense</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                    {getMonthlyData().map(([month, data]) => (
                      <React.Fragment key={month}>
                        <tr 
                          onClick={() => setExpandedMonth(expandedMonth === month ? null : month)}
                          className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors"
                        >
                          <td className="px-4 py-3 font-medium text-ink dark:text-white flex items-center gap-2">
                            <Icon name={expandedMonth === month ? 'chevron_down' : 'chevron_right'} size={16} className="text-slate-400" />
                            {month}
                          </td>
                          <td className="px-4 py-3 text-right font-bold text-red-500">-₹{Number(data.amount).toLocaleString()}</td>
                        </tr>
                        {expandedMonth === month && (
                          <tr>
                            <td colSpan={2} className="bg-slate-50/30 dark:bg-slate-900/30 p-0 border-t border-slate-100 dark:border-slate-800">
                              <div className="max-h-60 overflow-y-auto p-4 space-y-2">
                                {data.transactions.map(txn => (
                                  <div key={txn.id} className="flex justify-between items-center text-xs p-2 bg-white dark:bg-slate-800 rounded border border-slate-100 dark:border-slate-700">
                                    <div>
                                      <div className="font-semibold text-ink dark:text-slate-200">{txn.description || txn.category}</div>
                                      <div className="text-slate-500">{formatDateByPreference(txn.date)} · {txn.category}</div>
                                    </div>
                                    <div className="font-bold text-red-500">-₹{Number(txn.amount).toLocaleString()}</div>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">No expense data available</div>
            )}
          </div>
        </Modal>
      )}

      {/* Category-wise Report Modal */}
      {showCategoryReport && (
        <Modal title="Category-wise Report" onClose={() => { setShowCategoryReport(false); setExpandedCategory(null); }}>
          <div className="space-y-3">
            {getCategoryData().length > 0 ? (
              <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Category</th>
                      <th className="px-4 py-3 font-semibold text-right">Income</th>
                      <th className="px-4 py-3 font-semibold text-right">Expense</th>
                      <th className="px-4 py-3 font-semibold text-right">Net</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                    {getCategoryData().map(([category, data]) => (
                      <React.Fragment key={category}>
                        <tr 
                          onClick={() => setExpandedCategory(expandedCategory === category ? null : category)}
                          className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors"
                        >
                          <td className="px-4 py-3 font-medium text-ink dark:text-white flex items-center gap-2">
                            <Icon name={expandedCategory === category ? 'chevron_down' : 'chevron_right'} size={16} className="text-slate-400" />
                            {category}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-emerald-600">{data.Income > 0 ? `+₹${Number(data.Income).toLocaleString()}` : '-'}</td>
                          <td className="px-4 py-3 text-right font-semibold text-red-500">{data.Expense > 0 ? `-₹${Number(data.Expense).toLocaleString()}` : '-'}</td>
                          <td className={`px-4 py-3 text-right font-bold ${(data.Income - data.Expense) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                            {(data.Income - data.Expense) >= 0 ? '+' : '-'}₹{Math.abs(data.Income - data.Expense).toLocaleString()}
                          </td>
                        </tr>
                        {expandedCategory === category && (
                          <tr>
                            <td colSpan={4} className="bg-slate-50/30 dark:bg-slate-900/30 p-0 border-t border-slate-100 dark:border-slate-800">
                              <div className="max-h-60 overflow-y-auto p-4 space-y-2">
                                {data.transactions.map(txn => (
                                  <div key={txn.id} className="flex justify-between items-center text-xs p-2 bg-white dark:bg-slate-800 rounded border border-slate-100 dark:border-slate-700">
                                    <div>
                                      <div className="font-semibold text-ink dark:text-slate-200">{txn.description || txn.category}</div>
                                      <div className="text-slate-500">{formatDateByPreference(txn.date)}</div>
                                    </div>
                                    <div className={`font-bold ${txn.type === 'Income' ? 'text-emerald-600' : 'text-red-500'}`}>
                                      {txn.type === 'Income' ? '+' : '-'}₹{Number(txn.amount).toLocaleString()}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">No category data available</div>
            )}
          </div>
        </Modal>
      )}

      {/* Form Modal */}
      {showFormModal && (
        <Modal title={editIdx !== null ? t("title_edit_txn") : t("btn_add_txn")} onClose={() => setShowFormModal(false)}>
          {renderForm()}
        </Modal>
      )}

      {/* FAB for Mobile */}
      {createPortal(
        <div className="fixed bottom-6 right-6 z-50 lg:hidden pointer-events-auto">
          <button
            onClick={() => {
              setEditIdx(null);
              setForm({ ...def, created_by: user?.name || "" });
              setErrors({});
              setShowFormModal(true);
            }}
            className="w-14 h-14 bg-primary text-white rounded-full flex items-center justify-center shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:scale-105 transition-transform active:scale-95 border-0 cursor-pointer"
          >
            <Icon name="plus" size={24} />
          </button>
        </div>,
        document.body
      )}

    </div>
  );
}
